<script setup lang="ts">
import { computed } from 'vue'
import {
  NXGDSStatusTag,
  NXGDSStatusTagVariants,
  NXGDSCondensedIndicator,
  NXGDSCondensedIndicatorVariants,
} from '@nsftx/seven-nxg-design-system'
import { useBetslip, useGame, useGamesStore } from '@nsftx/seven-client-retail-vue-sgp'
import i18n from '@/plugins/translationsPlugin'
import type { Bet } from '@nsftx/seven-client-retail-vue-integrator'
import { formatDateTimeToLocaleString } from '@/utils/dateTimeHelpers'

const { t } = i18n.global

const { betslipId, bets, isMultibet, betslipPlacedAt, betslipProductId, getBetType } = useBetslip()
const { getGameById, getGameLinkByDisplayId } = useGamesStore()

const game = getGameById(betslipProductId.value!)
const gameLink = getGameLinkByDisplayId(game!.displayId!)
const { gameDisplayName } = useGame(gameLink)

const getGameTitle = (bet: Bet) => {
  const betType = getBetType(bet)

  return `${gameDisplayName.value} (${t(`bet_type_${betType.toLowerCase()}`)})`
}

const statusLabelData = computed(() => {
  if (!bets.value) {
    return {
      label: '',
      variant: NXGDSStatusTagVariants.attention,
    }
  }

  if (bets.value[0]?.status.phase === 'PAID_OUT') {
    return {
      label: bets.value[0].status.phase,
      variant: NXGDSStatusTagVariants.positive,
    }
  }

  if (bets.value[0]?.status.resolutionStatus === 'LOST') {
    return {
      label: bets.value[0].status.resolutionStatus,
      variant: NXGDSStatusTagVariants.negative,
    }
  }

  return {
    label: bets.value[0].status.resolutionStatus,
    variant: NXGDSStatusTagVariants.attention,
  }
})

const modalTitle = computed(() => {
  if (!bets.value?.length) {
    return ''
  }

  if (isMultibet.value) {
    return t('bet_type_multibet', { amount: bets.value.length })
  }

  return getGameTitle(bets.value[0])
})
</script>

<template>
  <div class="ss-slip-details-modal__header">
    <div class="ss-slip-details-modal__header-title">
      <h4>{{ modalTitle }}</h4>
      <NXGDSStatusTag
        v-if="!isMultibet"
        :label="statusLabelData.label"
        :variant="statusLabelData.variant"
      />
    </div>
    <div class="ss-slip-details-modal__header-subtitle">
      {{ formatDateTimeToLocaleString(betslipPlacedAt) }}
      <NXGDSCondensedIndicator :variant="NXGDSCondensedIndicatorVariants.universal" />
      {{ t('slip_check_details_betslip_id') }}: {{ betslipId }}
    </div>
  </div>
</template>

<style lang="scss">
@use '@nsftx/seven-nxg-design-system/style/mixins' as *;

.ss-slip-details-modal {
  &__header {
    display: flex;
    justify-content: space-between;
    gap: var(--nxgds-alias-spacing-xs);
    flex-direction: column;

    &-title {
      display: flex;
      align-items: center;
      gap: var(--nxgds-alias-spacing-xs);
      @include typography('h4', 'regular');
    }

    &-subtitle {
      display: flex;
      align-items: center;
      gap: var(--nxgds-alias-spacing-xs);
      color: var(--nxgds-mapped-color-text-secondary);
      @include typography('label', 'regular');
    }
  }
}
</style>
